// Generic shared per-key store w/ refcount-driven stream lifecycle.
// Used as the spine for orderWhales / liqMap / news shared transports —
// each one provides `startStream(key)` and `stopStream(key)` for its
// underlying poller/WS, and gets back a stable `useShared(key)` selector.
//
// `key` is a string ("BTC" for orderWhales/news, "hyperliquid|BTC" for
// liq map) so consumers can pack tuples without us caring about shape.

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

export function createSharedStore({ name, emptyValue, startStream, stopStream }) {
  const slices = new Map(); // key -> { value, version }
  const listeners = new Map(); // key -> Set<fn>
  const refs = new Map(); // key -> refcount

  function ensure(key) {
    if (!slices.has(key)) slices.set(key, { value: emptyValue(key), version: 0 });
  }

  function notify(key) {
    const set = listeners.get(key);
    if (!set) return;
    for (const fn of [...set]) {
      try { fn(); } catch { /* ignore listener errors */ }
    }
  }

  function set(key, value) {
    ensure(key);
    const cur = slices.get(key);
    slices.set(key, { value, version: cur.version + 1 });
    notify(key);
  }

  function get(key) {
    ensure(key);
    return slices.get(key).value;
  }

  function subscribe(key, listener) {
    let set = listeners.get(key);
    if (!set) { set = new Set(); listeners.set(key, set); }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(key);
    };
  }

  function acquire(key) {
    if (!key) return;
    const next = (refs.get(key) || 0) + 1;
    refs.set(key, next);
    ensure(key);
    if (next === 1) startStream(key, { set, get });
  }

  function release(key) {
    if (!key) return;
    const next = (refs.get(key) || 0) - 1;
    if (next <= 0) {
      refs.delete(key);
      stopStream(key);
      // Keep the slice in-place — re-acquire shows last-known data instantly.
    } else {
      refs.set(key, next);
    }
  }

  // useSyncExternalStore needs stable snapshot identity. Versioned wrapper
  // ensures referential equality when nothing changed.
  function makeGetSnapshot(key, lastRef) {
    return () => {
      ensure(key);
      const slice = slices.get(key);
      if (lastRef.current?.version === slice.version) return lastRef.current.value;
      lastRef.current = { value: slice.value, version: slice.version };
      return slice.value;
    };
  }

  function useShared(key) {
    useEffect(() => {
      if (!key) return undefined;
      acquire(key);
      return () => release(key);
    }, [key]);

    const lastRef = useRef(null);
    const subscribeForKey = useCallback(
      (cb) => (key ? subscribe(key, cb) : () => {}),
      [key]
    );
    const getSnapshot = makeGetSnapshot(key, lastRef);
    return useSyncExternalStore(subscribeForKey, getSnapshot, getSnapshot);
  }

  // Expose refs on window for debugging — same-module-instance check.
  if (typeof window !== 'undefined') {
    window.__cockpitSharedStores = window.__cockpitSharedStores || {};
    window.__cockpitSharedStores[name] = { refs, slices };
  }

  return {
    name, // for debugging
    useShared,
    _stats() {
      return {
        refs: Object.fromEntries(refs),
        cachedKeys: [...slices.keys()],
      };
    },
  };
}
